import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { RRule, Options as RRuleOptions } from 'rrule'
import { convertToRRuleOptions } from './rruleCompat'

dayjs.extend(utc)
dayjs.extend(timezone)

export interface AlignedOccurrence {
  start: Date
  end: Date
}

/**
 * 반복 일정 저장 시, 사용자가 지정한 시작일(DTSTART)과 RRULE이 실제로 만들어내는
 * 첫 번째 발생일이 다를 경우 보정된 { start, end } 를 계산한다.
 *
 * rrule.js는 dtstart의 UTC getter(getUTCFullYear/getUTCDay 등)로 캘린더 연산을
 * 수행하므로, 실제 절대시각(tz offset 포함)을 가진 Date를 그대로 dtstart로 넘기면
 * 자정 부근 이벤트에서 요일이 하루 밀릴 수 있다(rrule.js의 잘 알려진 함정). 이를
 * 피하기 위해 startTz 기준의 로컬 벽시계 성분(년/월/일/시/분/초)을 추출해 "가짜 UTC"
 * Date를 dtstart로 사용하고, 계산된 첫 발생일도 같은 프레임에서 해석한 뒤 원래 startTz로
 * 되돌린다. 이 tz 처리 관례는
 * features/calendar/src/components/EventEditor/recurrenceSplit.ts 의
 * `dayjs(date).tz(tz)` 패턴을 따른 것이다.
 *
 * @param startDate 사용자가 지정한 시작일시(절대시각)
 * @param startTz startDate를 해석할 타임존(IANA tz name)
 * @param options rrule.js 형식(Partial<RRuleOptions>) 또는 node-ical camelCase 형식
 *   — 내부에서 convertToRRuleOptions로 정규화한다
 * @param durationMinutes 시작~종료 간 길이(분). 보정된 end 계산에 사용
 * @returns 보정이 필요하면 { start, end }, 이미 일치하거나 계산할 수 없으면 null
 *   (throw 하지 않고 항상 null로 흡수 — 실패 시 기존 동작 유지)
 */
export function alignStartToFirstOccurrence(
  startDate: Date,
  startTz: string,
  options: Partial<RRuleOptions>,
  durationMinutes: number,
): AlignedOccurrence | null {
  try {
    const zonedStart = dayjs(startDate).tz(startTz)

    const fakeUtcStart = new Date(
      Date.UTC(
        zonedStart.year(),
        zonedStart.month(),
        zonedStart.date(),
        zonedStart.hour(),
        zonedStart.minute(),
        zonedStart.second(),
        zonedStart.millisecond(),
      ),
    )

    const normalizedOptions = convertToRRuleOptions(options as Record<string, unknown>)
    const rrule = new RRule({ ...normalizedOptions, dtstart: fakeUtcStart })
    const firstOccurrenceUtc = rrule.after(fakeUtcStart, true)

    if (!firstOccurrenceUtc || firstOccurrenceUtc.getTime() === fakeUtcStart.getTime()) {
      // 첫 발생일이 없거나(예: UNTIL이 시작일보다 이름) 이미 일치 — 보정 불필요
      return null
    }

    const occurrenceUtc = dayjs.utc(firstOccurrenceUtc)
    const correctedStart = zonedStart
      .year(occurrenceUtc.year())
      .month(occurrenceUtc.month())
      .date(occurrenceUtc.date())
      .toDate()

    const correctedEnd = new Date(correctedStart.getTime() + durationMinutes * 60 * 1000)

    return { start: correctedStart, end: correctedEnd }
  } catch {
    // 정규화/RRule 생성 실패 등 어떤 예외 상황에서도 throw 하지 않고 보정 스킵
    return null
  }
}
